import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { select, Store } from '@ngrx/store'
import { Observable } from 'rxjs'
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators'
import { ErrorReportingService } from '../../../shared/services/error-reporting.service'
import { DestinationType, IndexedCollection, PayloadedAction } from '../../../shared/types'
import { AAAStore } from '../../../store/root-reducer'
import { selectActiveTowLimitRestriction } from '../../advisories/advisories.selectors'
import { TowLimitRestriction } from '../../advisories/advisories.types'
import { selectIsRapUser, selectMaxTowMileLimit } from '../../auth/auth.selectors'
import {
  selectActiveCallStatus,
  selectIsDestinationSearchRequired
} from '../../dashboard/calls-statuses/call-status.selectors'
import { selectIsAccidentSituation, selectIssueOptionSelected } from '../../issue/issue.selectors'
import { ASSIGN_EXISTING_VEHICLE, SET_VEHICLE } from '../../member/member.actions'
import { selectMemberActiveVehicle } from '../../member/member.selectors'
import { RapService } from '../../rap/rap.service'
import { AdobeEventService } from '../../tagging/adobe/event-adobe.service'
import events from '../../tagging/events'
import { TaggingService } from '../../tagging/tagging.service'
import { AdobeEventTypes } from '../../tagging/tagging.types'
import { cancelStepsAhead, openMessageDialog } from '../../ui/ui.actions'
import { MessageDialogTypes } from '../../ui/ui.types'
import { FUEL_TYPE_ELECTRIC } from '../../vehicle/vehicle.utils'
import { NAVIGATE_NEXT_STEP } from '../../wizard/wizard.actions'
import { SET_LOCATION_CLUB } from '../location.actions'
import { selectBreakdownLocation, selectBreakdownLocationCoordinates, selectCountryClub } from '../location.selectors'
import { GenericCoordinates, TowLocation } from '../location.types'
import { aarAppendLocationMarker, LocationUtils } from '../location.utils'
import { NON_AAR_TOWING_NAMES, RESET_TOW_DESTINATION, resetTowDestination, setAARAddress } from '../tow-location/tow-location.actions'
import { selectTowLocationPreview } from '../tow-location/tow-location.selectors'
import {
  AAR_LOAD,
  completeAarLoad,
  notifyAarLoadFailure,
  requestAars,
  requestSearchArea,
  SEARCH_AREA,
  setAarPreview,
  setSuggestedShop
} from './aar.actions'
import { TOW_DESTINATION_LIMIT } from './aar.reducer'
import { selectAARParams, selectCurrentVehicleSlug, selectDestinationType, selectNearbyAARs, } from './aar.selectors'
import { AarService } from './aar.service'
import { AARData, AARRequestParams, SearchType } from './aar.types'
import { AarMatch, AarMatchType, buildAarMatchEventDetail, filterAarByServiceOffers, findFacilityMatch } from './aar.utils'

@Injectable()
export class AarEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<AAAStore>,
    private _aarService: AarService,
    private errorReportingService: ErrorReportingService,
    private rapService: RapService,
    private locationUtils: LocationUtils,
    private taggingService: TaggingService,
    private adobeEventService: AdobeEventService,
  ) {}

  refreshAars$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        SET_LOCATION_CLUB.SUCCESS,
        ASSIGN_EXISTING_VEHICLE,
        SET_VEHICLE,
        NAVIGATE_NEXT_STEP,
      ),
      tap(() => this.store$.dispatch(cancelStepsAhead())),
      withLatestFrom(
        this.store$.pipe(select(selectIsDestinationSearchRequired)),
        this.store$.pipe(select(selectBreakdownLocation)),
        this.store$.pipe(select(selectMemberActiveVehicle)),
        this.store$.pipe(select(selectCurrentVehicleSlug)),
        this.store$.pipe(select(selectActiveCallStatus)),
      ),
      switchMap(([_, isDestinationSearchRequired, breakdownLocation, activeVehicle, vehicleSlug, activeCallStatus]) => {
        const actions = []
        if (activeCallStatus === null) {
          actions.push(resetTowDestination())
        }
        if (isDestinationSearchRequired) {
          actions.push(requestAars({
            payload: {
              latitude: breakdownLocation.latitude,
              longitude: breakdownLocation.longitude,
              make: activeVehicle.make,
              model: activeVehicle.model,
              vehicleSlug,
              searchType: SearchType.BREAKDOWN_LOCATION,
            },
          }))
        }
        return actions
      })
    )
  )

  searchArea$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof requestSearchArea>>(SEARCH_AREA.REQUEST),
      withLatestFrom(
        this.store$.pipe(select(selectMemberActiveVehicle)),
        this.store$.pipe(select(selectCurrentVehicleSlug))
      ),
      switchMap(([action, activeVehicle, vehicleSlug]) => [
        setAarPreview({ payload: { id: null } }),
        requestAars({
          payload: {
            latitude: action.payload.center.latitude,
            longitude: action.payload.center.longitude,
            make: activeVehicle.make,
            model: activeVehicle.model,
            vehicleSlug,
            searchType: SearchType.CUSTOM_LOCATION,
            ...(action.payload.limit ? { limit: action.payload.limit } : {})
          },
        })
      ])
    )
  )

  loadAars$ = createEffect(
    (): Observable<
      | ReturnType<typeof completeAarLoad>
      | ReturnType<typeof notifyAarLoadFailure>
    > =>
      this.actions$.pipe(
        ofType<ReturnType<typeof requestAars>>(AAR_LOAD.REQUEST),
        filter((action) => Boolean(action.payload)),
        withLatestFrom(
          this.store$.pipe(select(selectIssueOptionSelected)),
          this.store$.pipe(select(selectDestinationType)),
          this.store$.pipe(select(selectMaxTowMileLimit)),
          this.store$.pipe(select(selectBreakdownLocationCoordinates)),
          this.store$.pipe(select(selectTowLocationPreview)),
          this.store$.pipe(select(selectCountryClub)),
          this.store$.pipe(select(selectMemberActiveVehicle)),
          this.store$.pipe(select(selectIsAccidentSituation)),
          this.store$.pipe(select(selectActiveTowLimitRestriction)),
        ),
        switchMap(([action, issueOptionSelected, destinationType, maxTowMileLimit, breakdownLocation, towLocation, country, vehicle, isAccidentSituation, towLimitRestriction]) => {
          // A peak-volume tow restriction replaces the member/RAP limit as search radius.
          const searchRadius = towLimitRestriction?.maxMiles ?? maxTowMileLimit
          return this._aarService.getAARs$({
            ...action.payload,
            ...(searchRadius ? { radius: searchRadius } : {}),
            ...(action.payload.limit ? { limit: action.payload.limit } : { limit: TOW_DESTINATION_LIMIT }),
            country,
            ...(vehicle?.fuelType === FUEL_TYPE_ELECTRIC ? { type: 'EV_DEALER' } : {}),
            ...(isAccidentSituation ? { isCollision: true } : {}),
          }, destinationType).pipe(
            map((aars) => filterAarByServiceOffers(issueOptionSelected, aars, this.rapService.isRapUser())),
            tap((aars) => this.recalculateDistanceTo(aars, breakdownLocation)),
            map((aars) => this.filterByTowLimitRestriction(aars, towLimitRestriction)),
            switchMap((aars) => {
              const actions: PayloadedAction[] = [
                completeAarLoad({
                  payload: {
                    [action.payload.vehicleSlug]: aars,
                  },
                })
              ]
              if (towLocation?.location?.name === NON_AAR_TOWING_NAMES.CUSTOM) {
                const aarMatch = findFacilityMatch(Object.values(aars), towLocation.location)
                const shop = aarAppendLocationMarker(aarMatch?.facility)
                if (aarMatch && shop) {
                  actions.push(setAarPreview({ payload: { id: aarMatch.facility.id } }))
                  actions.push(setAARAddress({
                    payload: shop,
                  }))
                }
                this.notifyAarMatchAnalytics(towLocation.location, aarMatch)
              }
              return actions
            }
            ),
            catchError((error) =>
              this.errorReportingService.notifyErrorObservable(
                error,
                notifyAarLoadFailure
              )
            )
          )
        }),
        catchError((error) =>
          this.errorReportingService.notifyErrorObservable(
            error,
            notifyAarLoadFailure
          )
        )
      )
  )

  handleNoFacilitiesFound$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AAR_LOAD.SUCCESS),
      withLatestFrom(
        this.store$.pipe(select(selectIsRapUser)),
        this.store$.pipe(select(selectAARParams)),
      ),
      filter(([action, isRapUser, aarParams]: [PayloadedAction, boolean, AARRequestParams]) =>
        !isRapUser
        && !Object.keys(Object.values(action.payload)[0]).length
        && SearchType.BREAKDOWN_LOCATION === aarParams?.searchType
      ),
      map(() =>
        openMessageDialog({
          payload: {
            type: MessageDialogTypes.NO_FACILITIES_FOUND,
          },
        })
      )
    )
  )

  handleSetSuggestedShop$ = createEffect(() =>
    this.actions$.pipe(
      ofType<
        | ReturnType<typeof completeAarLoad>
        | ReturnType<typeof resetTowDestination>
      >(
        AAR_LOAD.SUCCESS,
        RESET_TOW_DESTINATION
      ),
      withLatestFrom(
        this.store$.pipe(select(selectDestinationType)),
        this.store$.pipe(select(selectNearbyAARs)),
        this.store$.pipe(select(selectAARParams)),
      ),
      filter(([_, destinationType, aars, aarParams]: [PayloadedAction, DestinationType, AARData[], AARRequestParams]) =>
        SearchType.BREAKDOWN_LOCATION === aarParams?.searchType
        && aars.length > 0
      ),
      map(([_, destinationType, aars]: [PayloadedAction, DestinationType, AARData[], AARRequestParams]) =>
        setSuggestedShop({
          payload: aars[0]
        })
      )
    )
  )

  // A peak-volume tow restriction bounds the facility list regardless of how
  // the backend treats the search radius: anything beyond maxMiles from the
  // breakdown location is dropped before reaching the store.
  filterByTowLimitRestriction(
    aars: IndexedCollection<AARData>,
    towLimitRestriction: TowLimitRestriction | null
  ): IndexedCollection<AARData> {
    if (!towLimitRestriction || !aars || typeof aars !== 'object') {
      return aars
    }

    return Object.entries(aars)
      .filter(([, aar]) => aar.distanceTo <= towLimitRestriction.maxMiles)
      .reduce((filtered, [id, aar]) => {
        filtered[id] = aar
        return filtered
      }, {} as IndexedCollection<AARData>)
  }

  recalculateDistanceTo(aars: IndexedCollection<AARData>, breakdownLocationCoordinates: GenericCoordinates) {
    if (!aars || typeof aars !== 'object') {
      return
    }

    Object.keys(aars).forEach((id) => {
      if (Object.prototype.hasOwnProperty.call(aars, id)) {
        const aar = aars[id];
        if (aar && typeof aar === 'object') {
          aar.distanceTo = this.locationUtils.haversineMiles(aar, breakdownLocationCoordinates)
        }
      }
    })
  }

  private notifyAarMatchAnalytics(enteredLocation: TowLocation, match: AarMatch | null) {
    if (!enteredLocation?.streetNumber || !match) {
      return
    }

    const { facility, matchType } = match
    const event = matchType === AarMatchType.ADDRESS_COMPARISON
      ? events.towTo.AAR_MATCH_ADDRESS_COMPARISON
      : events.towTo.AAR_MATCH_COORDINATES

    const detail = buildAarMatchEventDetail(enteredLocation, facility)
    this.taggingService.setAutomatedEvent(event, events.towTo.DESTINATION_PAGE_TYPE, null, { 'Event Detail': detail })
    this.adobeEventService.sendEvent({ eventName: AdobeEventTypes.SYSTEM, eventValue: event }, null, { event_detail: detail })
  }
}
